import React from "react";
import { useTranslation } from "react-i18next";
import {
    StyledModal,
    StyledH2,
    StyledLabel,
    CheckboxIcon,
} from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { useSnapshot } from "valtio";

import { setGlobalDataIsUpdated, setIsEdited } from "../store";
import { store } from "@components/VerticalLayout/store";
import { store as LanguageStore } from "../store";

import { capitalize } from "@helpers/general";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { languages } from "@constants/index";

type ConfirmationModificationMessageType = {
    isModalOpened: boolean;
    setIsModalOpened: Function;
    setIsAddLanguageClicked: Function;
    associated: any[];
    isAddLanguageClicked: boolean;
    setDataIsUpdated: Function;
    dataIsUpdated: number | boolean;
    uploadedImage: any;
};

export function ConfirmationModificationModal({
    isModalOpened,
    setIsModalOpened,
    setIsAddLanguageClicked,
    associated,
    isAddLanguageClicked,
    setDataIsUpdated,
    dataIsUpdated,
    uploadedImage,
}: ConfirmationModificationMessageType) {
    const { t } = useTranslation();
    const { userID, franchiseID, operatorID } = useSnapshot(store);
    const { globalDataIsUpdated, editedDataLanguage } = useSnapshot(
        LanguageStore
    );

    const [error, setError] = React.useState<boolean>(false);
    const uuidUser = localStorage.getItem("uuidUser");
    async function editLanguage() {
        toast.dismiss();
        let objectData = {};
        let localData: any[] = [];
        associated.forEach(async (el: any) => {
            const id: any = el.Id;
            Object.assign(objectData, {
                [id]: {
                    id: id,
                    language: languages.find(
                        (lg: any) =>
                            lg.value ===
                            editedDataLanguage.data[id].abbreviation
                    )?.label,
                    display_name: editedDataLanguage.data[id].display_name,
                    country: editedDataLanguage.data[id].country,
                    urlImage: editedDataLanguage.data[id].urlImage,
                    isArchived: el.isArchived,
                    isFavorite: el.isFavorite,
                    isActive: el.isActive,
                    shopId: el.shopId,
                    abbreviation: el.abbreviation,
                    advancedDisplayName:
                        editedDataLanguage.data[id].advancedDisplayName,
                },
            });
            let dataLanguage = {
                userId: userID,
                shopId: [el.shopId],
                franchiseId: franchiseID,
                operatorId: operatorID,
                uuidUser: uuidUser,
                data: objectData,
            };
            const data = new FormData();
            data.append("dataLanguage", JSON.stringify(dataLanguage));
            data.append("image", uploadedImage);
            localData.push(
                fetch(
                    `${process.env.REACT_APP_API_V2_URL}/settings/general/language`,
                    {
                        method: "POST",
                        body: data,
                    }
                ).then((response) => response.json())
            );
        });
        try {
            mutate(
                `${process.env.REACT_APP_API_V2_URL}/settings/general/language`,
                await Promise.all(localData).then((result) => {
                    result.forEach((element: any) => {
                        if (element.error === true) setError(true);
                    });
                })
            );
        } catch (e) {
            toast.error(`${t("There's an error")}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
        if (error === false) {
            toast.success(
                `${t("The language of shop  was successfully changed")}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
            setDataIsUpdated(!dataIsUpdated);
            setIsAddLanguageClicked(!isAddLanguageClicked);
            setGlobalDataIsUpdated(!globalDataIsUpdated);
        }
    }

    return (
        <StyledModal toggle={true} isOpen={isModalOpened!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsModalOpened(!isModalOpened);
                    setDataIsUpdated(!dataIsUpdated);
                    setIsAddLanguageClicked(false);
                }}
            >
                <StyledH2>{capitalize(t("Alert"))}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        'Do you want to apply these changes for all stores? \n Attention ! If you click on "Validate", the specific settings of the shops will be modified.'
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                        setIsAddLanguageClicked(false);
                        setDataIsUpdated(!dataIsUpdated);
                        setIsEdited(false);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={() => {
                        editLanguage();
                        setIsModalOpened(!isModalOpened);
                        setIsAddLanguageClicked(false);
                        setIsEdited(false);
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
